import { Controller } from "@hotwired/stimulus"
import balancedRows from "../lib/balanced_rows"

// 並べた結果を element の id をキーに localStorage へ覚える。次に開いたとき、ビューのインラインスクリプトが
// 同じ画面幅・同じ signature なら <style id="<id>-layout"> で描画の前に当て、無ければ並べ終えるまで隠す
// (リロードでは Web フォントの読み込みより先に描画されるので、ここで並べるのを待つと隠れている間が点滅に見える)
// Connects to data-controller="balanced-rows"
export default class extends Controller {
  static values = { signature: String }

  #observer
  #pending
  #laidOutWidth

  connect() {
    // 代替フォントの幅で並べると Web フォント適用後の並べ直しが見えるので、チップの文字に要るフォントを読み込んでから並べる
    // (document.fonts.ready はフォントの要求が始まる前だとすぐ解決する)。読み込めない (遮断・オフライン) ときは代替フォントのまま並べる
    const { fontWeight, fontSize, fontFamily } = getComputedStyle(this.element)
    document.fonts.load(`${fontWeight} ${fontSize} ${fontFamily}`, this.element.textContent).then(this.#start, this.#start)
  }

  #start = () => {
    // 待つ間に接続し直されると then が重なるので、observer を 2 つ作らない
    if (!this.element.isConnected || this.#observer) return

    this.#observer = new ResizeObserver(this.#layoutIfWidthChanged)
    this.#observer.observe(this.element)
    // 後から別のフォントが読み込まれて字幅が変わったときも並べ直す
    document.fonts.addEventListener("loadingdone", this.#scheduleLayout)
    this.#layout()
  }

  disconnect() {
    this.#observer?.disconnect()
    this.#observer = null
    cancelAnimationFrame(this.#pending)
    document.fonts.removeEventListener("loadingdone", this.#scheduleLayout)
  }

  // 並べた後の高さの変化でも呼ばれるので、幅が変わったときだけ並べ直す
  #layoutIfWidthChanged = () => {
    if (this.element.getBoundingClientRect().width !== this.#laidOutWidth) this.#scheduleLayout()
  }

  #scheduleLayout = () => {
    cancelAnimationFrame(this.#pending)
    this.#pending = requestAnimationFrame(this.#layout)
  }

  #layout = () => {
    document.querySelectorAll(`style#${this.element.id}-layout`).forEach(style => style.remove())
    const chips = [...this.element.children]
    for (const chip of chips) chip.style.removeProperty("flex")
    this.#laidOutWidth = this.element.getBoundingClientRect().width
    if (this.#laidOutWidth === 0) return

    const widths = chips.map(chip => chip.getBoundingClientRect().width)
    const gap = parseFloat(getComputedStyle(this.element).columnGap)
    const extras = balancedRows(widths, this.#laidOutWidth, gap).flatMap(({ count, extra }) => Array(count).fill(extra))
    chips.forEach((chip, index) => { chip.style.flex = `1 1 ${widths[index] + extras[index]}px` })
    this.#save(chips)
  }

  #save(chips) {
    const layout = { signature: this.signatureValue, viewportWidth: window.innerWidth, flex: chips.map(chip => chip.style.flex) }
    try {
      localStorage.setItem(this.element.id, JSON.stringify(layout))
    } catch (error) {
      // プライベートブラウズや容量の上限で保存できないときは、次に開いたとき並べ終えるまで隠れるだけ
      if (!(error instanceof DOMException)) throw error
    }
  }
}
